<script setup lang="ts" generic="L extends PejerTableNames, TValue, T extends PejerTablesList<L>">
import type {
  AccessorKeyColumnDef,
  Cell,
  CellContext,
  HeaderGroup,
  VisibilityState,
} from '@tanstack/vue-table'
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table'
import DataStateDisplay from '../../DataState/DataStateDisplay.vue'
import Skeleton from '../../ui/skeleton/Skeleton.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'

import type { BaseQueryArgs, ErrorObject, Order, PaginationArgs } from 'pejer/src/types/types'
import { computed, MaybeRef, onMounted, ref, unref, watch } from 'vue'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table'

import { ArrowDown, RefreshCcw } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import { cn } from 'pejer/src/lib/utils'
import { usePejerBase } from 'pejer/src/stores/pejerData'
import { JSSchema, JSSchemaObject, JSSchemaObjectTypes } from 'pejer/src/tables'
import { getNestedObject } from 'pejer/src/tables/helpers'
import { PejerTableNames, PejerTablesList } from 'pejer/src/types/base'
import { Groups } from '../../../tables'
import DataStateError from '../../DataState/DataStateError.vue'
import { DataTableEmits } from './DataTableEmits'
import FieldActions from './FieldActions.vue'
import FullDataItem from './FullDataItem.vue'
import HiddenColumns from './HiddenColumns.vue'
import { GroupedHeaders, GroupedVisibleCells } from './types'

const props = defineProps<{
  columns: AccessorKeyColumnDef<PejerTablesList<L>, TValue>[]
  data: PejerTablesList<L>[]
  pagination: PaginationArgs
  tableName: L
  canExpandRows?: boolean
  isLoading?: boolean
  totalSize?: number
  error?: MaybeRef<ErrorObject | null>
  isError?: MaybeRef<boolean>
  isFetching?: boolean
  query?: MaybeRef<BaseQueryArgs<PejerTablesList<L>>>
  initialColumnVisibility?: VisibilityState
  columnsVisibleByDefault?: MaybeRef<Record<string, boolean>>
  unsearchableColumns?: string[]
  groups?: Groups
  dontFillMissingEntries?: boolean
}>()

const emit = defineEmits<DataTableEmits<keyof T>>()

async function refresh() {
  emit('refresh')
}

// We need both of these as useVueTable will fill the rest of the state with false,
// therefore we need a separate ref to track the actual state of the columns.
// We do this to give the false state a meaning, as it means the column has been hidden by the user.
const visibleColumns = ref<VisibilityState>({ ...props.initialColumnVisibility })
const boundVisibleColumns = ref<VisibilityState>({ ...props.initialColumnVisibility })

const table = useVueTable({
  get data() {
    return props.data
  },
  get columns() {
    return props.columns
  },
  columnResizeMode: 'onChange',
  state: {
    get pagination() {
      return {
        pageIndex: props.pagination.page,
        pageSize: props.pagination.size,
      }
    },
    get columnVisibility() {
      return boundVisibleColumns.value
    },
  },
  getCoreRowModel: getCoreRowModel(),
  manualPagination: true,
})

const pejer = usePejerBase()
const schema = computed(() => pejer.getListSchema(props.tableName))

function getSchema(key: string): {
  originalSchema: JSSchemaObject
  schemaObj: JSSchemaObjectTypes
} | null {
  const originalSchema = pejer.getSchemaObjectFromFlattenedKeys(schema.value as JSSchema, key)
  const schemaObj = pejer.getSchemaReferencedObject(originalSchema)
  if (!schemaObj) {
    console.error(new Error(`Column '${key}' was not found in the schema: ${schema.value.name}`))
    return null
  }
  return { originalSchema, schemaObj }
}

/**
 * Toggles the sorting of a column. We want the toggle to cycle through the following states:
 * * 1. Ascending
 * * 2. Descending
 * * 3. Back to default column sorting
 */
function toggleSortBy(columnId: keyof T) {
  if (props.query && unref(props.query).sortBy === columnId) {
    if (!unref(props.query).sorter) return

    if (unref(props.query).sorter === 'Ascending') {
      const defaultColumn = pejer.getDefaultColumns(props.tableName)
      emit('triggerSort', defaultColumn.sortingColumn as keyof T, 'Descending')
      return
    }

    emit('triggerSort', columnId, 'Ascending')
  } else {
    emit('triggerSort', columnId)
  }
}

function resetColumnsVisible() {
  const updatedVisibilityState: VisibilityState = {}

  const propsColumnsVisibleByDefault = unref(props.columnsVisibleByDefault)
  if (propsColumnsVisibleByDefault) {
    Object.keys(propsColumnsVisibleByDefault).forEach((col) => {
      updatedVisibilityState[formatNestedColumnKey(col)] =
        propsColumnsVisibleByDefault[formatNestedColumnKey(col)]
    })
  }

  visibleColumns.value = { ...updatedVisibilityState }
  boundVisibleColumns.value = {
    ...boundVisibleColumns.value,
    ...visibleColumns.value,
  }

  table.getVisibleLeafColumns().forEach((col) => {
    const id = formatNestedColumnKey(col.id)
    if (id in visibleColumns.value === false) boundVisibleColumns.value[id] = false
  })

  emit('updateColumnVisibility', updatedVisibilityState)
}

onMounted(() => {
  const newBoundVisibleColumns = { ...props.initialColumnVisibility }
  table.getVisibleLeafColumns().forEach((col) => {
    const id = formatNestedColumnKey(col.id)
    if (id in visibleColumns.value === false) newBoundVisibleColumns[id] = false
  })
  boundVisibleColumns.value = newBoundVisibleColumns
})

watch(
  () => props.columnsVisibleByDefault,
  () => {
    resetColumnsVisible()
  }
)

const idToGroupMap = computed(() => {
  const map: Record<string, string> = {}
  table.getAllLeafColumns().forEach((col) => {
    const columnDef = col.columnDef as AccessorKeyColumnDef<T, TValue>
    // console.log(col.id, columnDef)
    // if (columnDef?.accessorKey)
    map[col.id] = columnDef.meta?.group ?? '_'
  })
  return map
})

const columnGroupsSize = computed(() => {
  const groups = new Set<string>()
  table.getAllLeafColumns().forEach((col) => {
    const group = col.columnDef.meta?.group ?? '_'
    groups.add(group)
  })
  return groups.size
})

function formatNestedColumnKey(key: string) {
  return key.replace('.', '_')
}

function columnIsMain(columnId: string) {
  if (!props.groups) return false
  if (!columnId) return false
  const mainColumns = Object.values(props.groups).map((g) => g.mainColumn) as string[]
  return mainColumns.includes(columnId)
}

function showColumn(col: string, show = true) {
  visibleColumns.value = {
    ...visibleColumns.value,
    [formatNestedColumnKey(col)]: show,
  }

  // Get the isMain column of the group this column belongs to, and ensure its visibility is also updated
  const group = table
    .getAllLeafColumns()
    .find(
      (c) => (c.columnDef as AccessorKeyColumnDef<PejerTablesList<L>, unknown>).accessorKey === col
    )?.columnDef.meta?.group

  // find the isMain column of this group
  const mainColumn = table
    .getAllLeafColumns()
    .find(
      (c) =>
        (c.columnDef.meta?.group === group && c.columnDef.meta.isMain) ||
        columnIsMain(
          (c.columnDef as AccessorKeyColumnDef<PejerTablesList<L>, unknown>).accessorKey as string
        )
    )

  if (mainColumn && show === true)
    visibleColumns.value = {
      ...visibleColumns.value,
      [formatNestedColumnKey(mainColumn.id)]: show,
    }

  boundVisibleColumns.value = {
    ...boundVisibleColumns.value,
    ...visibleColumns.value,
  }
  emit('updateColumnVisibility', visibleColumns.value)
}

function triggerSearch(column: string) {
  emit('updateColumnSearch', column as keyof T)
}

function shouldHideColumn(column: string) {
  return !!props.unsearchableColumns?.includes(column)
}

/**
 * * Gets the headers of a header group and groups them by their group id.
 */
function getGroupedHeaders(headerGroup: HeaderGroup<PejerTablesList<L>>): GroupedHeaders<T>[] {
  const result: GroupedHeaders<T>[] = []
  let currentGroup = null

  headerGroup.headers.forEach((header) => {
    const groupId = header.column.columnDef.meta?.group ?? '_'
    if (currentGroup && groupId === currentGroup.groupId) {
      // currentGroup.colspan += 1
    } else {
      if (currentGroup) {
        result.push(currentGroup)
      }
      currentGroup = {
        header,
        groupId,
        colspan: 1,
      }
    }
  })

  if (currentGroup) {
    result.push(currentGroup)
  }

  return result
}

// Add this line to create a reactive Set for expanded rows
const expandedRows = ref(new Set<string>())

/**
 * Toggles the expanded state of a row.
 */
function toggleRow(rowId: string) {
  if (!props.canExpandRows) return
  if (expandedRows.value.has(rowId)) {
    expandedRows.value.delete(rowId)
  } else {
    expandedRows.value.add(rowId)
  }
}

watch(
  () => props.data,
  () => {
    expandedRows.value = new Set<string>()
  }
)

function getGroupedVisibleCells(
  cells: Cell<PejerTablesList<L>, unknown>[],
  groupedHeaders: GroupedHeaders<PejerTablesList<L>>[]
): GroupedVisibleCells<PejerTablesList<L>>[] {
  const result: (GroupedVisibleCells<PejerTablesList<L>> | null)[] = groupedHeaders.map(
    (header) => {
      var cellsWithSameHeader = cells.filter(
        (cell) => (cell.column.columnDef.meta?.group ?? '_') === header.groupId
      )

      if (cellsWithSameHeader.length === 0) return null

      var mainCell =
        cellsWithSameHeader.find(
          (cell) =>
            cell.column.columnDef.meta?.isMain == true ||
            columnIsMain(
              (cell.column.columnDef as AccessorKeyColumnDef<PejerTablesList<L>, unknown>)
                .accessorKey as string
            )
        ) ?? cellsWithSameHeader[0]
      var supportingCells = cellsWithSameHeader.filter((cell) => cell !== mainCell)

      return {
        ...header,
        cells: {
          main: mainCell ?? null,
          supporting: supportingCells || [],
        },
      }
    }
  )

  return result.filter((r) => r !== null)
}

/**
 * @deprecated This function should not be used anymore, as passing state should not be done through props, but rather injected if needed.
 */
function mergeProps<T>(cellContext: CellContext<T, unknown>): CellContext<T, unknown> {
  return {
    ...cellContext,
    options: { oneLine: false },
    emits: {
      loadMore: (page: number, pageSize: number) => emit('loadMore', page, pageSize),
      // reload: async () => await emit('reload'),
      triggerSort: (column: string, sorter?: Order) => {
        emit('triggerSort', column as any, sorter as any)
      },
      updateColumnVisibility: (visibility: VisibilityState) =>
        emit('updateColumnVisibility', visibility),
      updateColumnSearch: (column: string) => emit('updateColumnSearch', column as any),
    },
  }
}
</script>
<template>
  <!-- <SectionCard> -->
  <HiddenColumns
    :columns="
      table
        .getAllLeafColumns()
        .map((col) => col.columnDef as AccessorKeyColumnDef<T, TValue>)
        .filter((col) => !col.meta?.hideFromTable)
    "
    :visibleColumns="boundVisibleColumns"
    :groupColors="props.groups"
    :unsearchableColumns="props.unsearchableColumns"
    @triggerSearch="triggerSearch"
    @resetDisplayedColumns="resetColumnsVisible"
    @showColumn="showColumn"
  />

  <div class="overflow-hidden">
    <TooltipProvider>
      <Table class="">
        <TableHeader>
          <TableRow class="border-0" v-if="columnGroupsSize != 1">
            <TableHead
              v-for="group in getGroupedHeaders(table.getHeaderGroups()[0])"
              :key="group.header.id"
              :class="
                cn('px-0 relative', {
                  'w-0 sticky right-0 bg-white/50 backdrop-blur-sm z-10':
                    group.header.column.id === 'actions' && columnGroupsSize > 1,
                })
              "
            >
              <!-- :colspan="group.colspan" -->
              <div
                v-if="group.header.column.id !== 'actions'"
                class="relative flex h-full items-center justify-between flex-1 w-full"
              >
                <span
                  v-if="group.header.column.columnDef.meta?.group"
                  class="sticky bottom-0 left-0 font-normal flex items-center"
                  :style="{
                    color:
                      props.groups?.[idToGroupMap[group.header.column.id]]?.color.accent ||
                      'lightGray',
                  }"
                >
                  <component
                    v-if="props.groups?.[idToGroupMap[group.header.column.id]]?.icon"
                    class="w-4 h-4 mr-1 shrink-0"
                    :is="props.groups?.[idToGroupMap[group.header.column.id]]?.icon"
                  ></component>
                  {{ idToGroupMap[group.header.column.id] }}
                </span>

                <div
                  v-for="cell in table.getAllLeafColumns().filter((cell) => {
                    // if (!cell.columnDef?.meta)
                    //   console.log(cell.columnDef, group.header.column.columnDef.meta?.group)
                    return (
                      cell.columnDef?.meta?.group &&
                      cell.columnDef.meta.group === group.header.column.columnDef.meta.group &&
                      (cell.columnDef as any).accessorKey === unref(query)?.sortBy
                    )
                  })"
                  :key="cell.id"
                >
                  <Button
                    variant="ghost"
                    class="text-right float-right mr-2 flex items-center gap-1 text-xs mt-1 p-1"
                    @click.stop.prevent="
                      toggleSortBy((cell.columnDef as any).accessorKey as keyof T)
                    "
                  >
                    {{ cell.columnDef.header }}
                    <ArrowDown
                      v-if="!isFetching"
                      :style="{
                        transition: 'all',
                        transform: unref(query)?.sorter === 'Ascending' ? 'rotate(180deg)' : '',
                      }"
                      :size="12"
                    />
                    <RefreshCcw v-else class="animate-spin scale-[-1]" :size="14" />
                  </Button>
                </div>
                <div
                  class="w-full h-1 absolute bottom-0 rounded-t-md"
                  v-if="group.header.column.columnDef.meta?.group"
                  :style="{
                    backgroundColor:
                      props.groups?.[idToGroupMap[group.header.column.id]]?.color.accent || '',
                  }"
                ></div>
              </div>
              <Button
                variant="ghost"
                v-else
                class="cursor-pointer hover:bg-gray-200 h-full text-center flex justify-center items-center rounded-sm w-full place-self-center"
                @click="refresh()"
              >
                <RefreshCcw :size="20" :class="isFetching ? 'animate-spin scale-[-1]' : ''" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <DataStateDisplay
            :isError="(unref(isError) || !!unref(error)) && pagination.page - 1 >= data.length"
            :error="error"
            :isLoading="isLoading"
          >
            <!-- Show skeleton loader -->
            <template #loading>
              <TableRow class="" v-for="rowIndex in pagination.size" :key="rowIndex">
                <TableCell class="h-12 p-2 align-middle" :colSpan="columns.length">
                  <Skeleton class="w-full h-[20px]" />
                </TableCell>
              </TableRow>
            </template>
            <template #error>
              <TableRow class="border-0" v-for="rowIndex in pagination.size - 1" :key="rowIndex">
                <TableCell :colspan="columns.length" class="h-12">
                  <DataStateError
                    v-if="rowIndex === Math.floor(pagination.size / 2)"
                    class="w-full flex"
                    :error="unref(error)"
                  />
                </TableCell>
              </TableRow>
              <!-- <td :colspan="columns.length"></td> -->
            </template>
            <!-- No results -->
            <template v-if="table.getRowModel().rows?.length === 0">
              <TableRow class="border-0" v-for="rowIndex in pagination.size" :key="rowIndex">
                <TableCell :colSpan="columns.length" class="h-12 text-center">
                  <span v-if="rowIndex === Math.floor(pagination.size / 2)">No results.</span>
                </TableCell>
              </TableRow>
            </template>
            <!-- Data rows -->
            <template v-else>
              <template v-for="row in table.getRowModel().rows" :key="(pagination.page * pagination.size) / 10 + row.id">
                <TableRow
                  @click="toggleRow(row.id)"
                  :class="{
                    'transition-transform bg-gray-100 border-b-0': expandedRows.has(row.id),
                    'transition-transform cursor-pointer hover:scale-[99.5%] ': props.canExpandRows,
                    'transition-transform hover:bg-white': !props.canExpandRows,
                  }"
                >
                  <TableCell
                    v-for="cellGroup in getGroupedVisibleCells(
                      row.getVisibleCells(),
                      getGroupedHeaders(table.getHeaderGroups()[0] as any) as any
                    )"
                    :key="cellGroup.header.id"
                    :class="
                      cn('py-3 px-2 items-start align-top', {
                        'w-0 sticky right-0 z-10 bg-white/50 backdrop-blur-sm ':
                          cellGroup.header.column.id === 'actions',
                      })
                    "
                  >
                    <div class="h-full" v-if="cellGroup.header.column.id === 'actions'">
                      <FlexRender
                        :render="cellGroup.cells.main.column.columnDef.cell"
                        :props="mergeProps(cellGroup.cells.main.getContext())"
                      />
                    </div>
                    <!-- maxWidth: cellGroup.header.id !== 'actions' ? cellGroup.column.getSize() + 'px' : '', -->
                    <div class="flex flex-col" v-else>
                      <h5 class="font-medium group flex items-center relative">
                        <Tooltip>
                          <TooltipTrigger as-child>
                            <span class="">
                              <FlexRender
                                :render="cellGroup.cells.main.column.columnDef.cell"
                                :props="mergeProps(cellGroup.cells.main.getContext())"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p class="text-sm">
                              {{
                                getNestedObject(
                                  cellGroup.cells.main.row.original,
                                  (cellGroup.cells.main.column.columnDef as any)
                                    .accessorKey as string
                                )
                              }}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                        <FieldActions
                          v-if="!cellGroup.cells.main.column.columnDef.meta?.hideActions"
                          :cell="cellGroup.cells.main"
                          :cellGroup="cellGroup"
                          :showColumn="showColumn"
                          :triggerSearch="triggerSearch"
                          :shouldHideColumn="shouldHideColumn"
                          :toggleSortBy="toggleSortBy"
                          :sortBy="unref(query)?.sortBy"
                          :sorter="unref(query)?.sorter"
                          :isFetching="isFetching"
                        />
                      </h5>
                      <template v-for="cell in cellGroup.cells.supporting" :key="cell.id">
                        <div
                          v-if="
                            !cell.column.columnDef.meta?.hideIfEmpty ||
                            getNestedObject(
                              cellGroup.cells.main.row.original,
                              (cellGroup.cells.main.column.columnDef as any).accessorKey as string
                            )
                          "
                          class="text-xs text-muted-foreground relative group text-ellipsis overflow-hidden whitespace-nowrap text-nowrap max-w-[300px]"
                        >
                          <Tooltip>
                            <TooltipTrigger as-child>
                              <span
                                v-if="
                                  (cell.getContext().column.columnDef as any).accessorKey &&
                                  !cell.column.columnDef.meta?.keepStyle
                                "
                              >
                                <template v-if="!cell.column.columnDef.meta?.hideHeaderInCell">
                                  {{ cell.column.columnDef.header }}:
                                </template>
                                <FlexRender
                                  :render="cell.column.columnDef.cell"
                                  :props="mergeProps(cell.getContext())"
                                />
                              </span>
                              <FlexRender
                                v-else
                                :render="cell.column.columnDef.cell"
                                :props="mergeProps(cell.getContext())"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p
                                class="text-sm"
                                v-if="(cellGroup.cells.main.column.columnDef as any)?.accessorKey"
                              >
                                {{
                                  getNestedObject(
                                    cell.row.original,
                                    (cell.column.columnDef as any).accessorKey as string
                                  )
                                }}
                              </p>
                            </TooltipContent>
                          </Tooltip>

                          <FieldActions
                            v-if="!cell.column.columnDef.meta?.hideActions"
                            :cell="cell"
                            :cellGroup="cellGroup"
                            :showColumn="showColumn"
                            :triggerSearch="triggerSearch"
                            :shouldHideColumn="shouldHideColumn"
                            :toggleSortBy="toggleSortBy"
                            :sortBy="unref(query)?.sortBy"
                            :sorter="unref(query)?.sorter"
                            :isFetching="isFetching"
                          />
                        </div>
                      </template>
                    </div>
                  </TableCell>
                </TableRow>
                <FullDataItem
                  v-if="props.canExpandRows"
                  :row="row"
                  :table-name="tableName"
                  :columns="columns"
                  :is-expanded="expandedRows.has(row.id)"
                />
              </template>
              <template v-if="!dontFillMissingEntries">
                <TableRow
                  v-for="index in pagination.size - table.getRowModel().rows?.length"
                  :key="index"
                >
                  <div class="h-10 p-1 align-middle w-full"></div>
                </TableRow>
              </template>
            </template>
          </DataStateDisplay>
        </TableBody>
      </Table>
    </TooltipProvider>
  </div>
  <!-- </SectionCard> -->
</template>
